import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { OutlinedInput, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { ModalContent } from '../../modal/components/ModalContent';
import { ModalActions } from '../../modal/components/ModalActions';
import { useModalContext } from '../../modal/ModalContext';
import { FormField } from '../../form/FormField';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const deletePersonaBlockMutation = graphql(`
	mutation persona_deletePersonaBlock($id: UUID!) {
		deletePersonaBlock(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (textToMatch: string, intl: IntlShape) =>
	z.object({
		confirmField: z.literal(textToMatch, {
			errorMap: () => ({ message: intl.formatMessage({ id: 'common.delete.invalid' }, { desired: textToMatch }) }),
		}),
	});

export const deletePersonaBlockModalSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export const DeletePersonaBlockModal: FC<z.infer<typeof deletePersonaBlockModalSchema>> = ({ id, name }) => {
	const { openSnackbar } = useSnackbar();
	const { mutate: deletePersona } = useApiMutation(deletePersonaBlockMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deletePersonaBlock.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'persona.block.delete.error' }),
				},
				errorMessage: result.deletePersonaBlock.errorMessage,
				openSnackbar,
			});
		},
	});
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const form = useTypedForm({
		schema: createFormSchema(name, intl),
	});

	return (
		<>
			<ModalContent isDelete title={intl.formatMessage({ id: 'persona.block.delete.confirmation' }, { name })} description={''}>
				<>
					<Typography textAlign={'center'}>
						<strong>
							<FormattedMessage id={'common.delete.confirmation'} />
						</strong>
					</Typography>
					<Stack width={'50%'} alignSelf={'center'}>
						<form noValidate>
							<FormField
								form={form}
								name={'confirmField'}
								label={''}
								render={(props) => (
									<OutlinedInput {...props} placeholder={intl.formatMessage({ id: 'common.delete.confirmation-placeholder' }, { name })} />
								)}
							/>
						</form>
					</Stack>
				</>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deletePersona({ id });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};
